import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  Category as CategoryIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { productAPI } from '../services/api';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await productAPI.getProductById(id);
      if (response.data.success) {
        setProduct(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch product details');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handleRequestQuote = () => {
    navigate(`/request-quote?product=${id}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/600x400?text=No+Image';
    if (imageUrl.startsWith('http')) return imageUrl;
    // Use base server URL without /api for static files
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
    const imagePath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${baseUrl}${imagePath}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">{error || 'Product not found'}</Alert>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Back to Products
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            Loha Mart
          </Typography>
          <Button color="inherit" onClick={() => navigate('/')}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate('/products')}>
            Products
          </Button>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        <Box py={4}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mb: 3 }}
          >
            Back to Products
          </Button>

          <Grid container spacing={4}>
            {/* Product Image */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardMedia
                  component="img"
                  image={getImageUrl(product.image_url)}
                  alt={product.name}
                  sx={{ maxHeight: 500, objectFit: 'contain' }}
                />
              </Card>
            </Grid>

            {/* Product Details */}
            <Grid item xs={12} md={6}>
              <Box>
                <Chip
                  icon={<CategoryIcon />}
                  label={product.category?.name || 'Uncategorized'}
                  color="primary"
                  sx={{ mb: 2 }}
                />

                <Typography variant="h4" gutterBottom>
                  {product.name}
                </Typography>

                <Typography variant="h5" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                  {product.price
                    ? `₹${product.price}`
                    : product.price_range_min && product.price_range_max
                      ? `₹${product.price_range_min} - ₹${product.price_range_max}`
                      : 'Price on request'}
                  {product.unit && ` / ${product.unit}`}
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {product.description || 'No description available'}
                </Typography>

                {product.unit && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>
                      Unit
                    </Typography>
                    <Typography variant="body1">
                      {product.unit}
                    </Typography>
                  </>
                )}

                {product.supplier && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <BusinessIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Supplier Information
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        <strong>Company:</strong> {product.supplier.company_name}
                      </Typography>
                      {product.supplier.city && (
                        <Typography variant="body2" color="text.secondary">
                          {product.supplier.city}, {product.supplier.state}
                        </Typography>
                      )}
                    </Paper>
                  </>
                )}

                <Box mt={4}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleRequestQuote}
                  >
                    Request Quote
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Category Description */}
          {product.category?.description && (
            <Box mt={4}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  About {product.category.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.category.description}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default ProductDetails;
